import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import LineWeight from '@mui/icons-material/LineWeight';
import Opacity from '@mui/icons-material/Opacity';
import RoundedCorner from '@mui/icons-material/RoundedCorner';
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRight from '@mui/icons-material/FormatAlignRight';
import RemoveIcon from '@mui/icons-material/Remove';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import TuneOutlined from '@mui/icons-material/TuneOutlined';

import type { LandingConfig } from './landingBuilderTypes';
import type { BuilderBlock, BuilderBorderStyle, BuilderSection } from './LandingPageCanvasEditor';
import { ColorPickerPopover, normalizeHex } from './ColorPickerPopover';
import { LANDING_CANVAS_FONT_OPTIONS, canvasFontIdFromStack } from './landingCanvasFonts';

/** Bo góc nút & khung chữ trong toolbar (px) */
const MAX_CORNER_RADIUS_PX = 100;

/** Nút chọn nhanh trong popover viền (giống Canva): không / liền / gạch / chấm / đôi */
type QuickBorderPick = 'none' | 'solid' | 'dashed' | 'dotted' | 'double';

function quickBorderFromModel(
  style: BuilderBorderStyle | undefined,
  width: number | undefined,
): QuickBorderPick {
  const w = width ?? 0;
  if (w <= 0 || style === 'none') return 'none';
  const s = style ?? 'solid';
  if (s === 'dashed' || s === 'dotted' || s === 'double') return s;
  return 'solid';
}

function BorderStyleGlyph({ pick }: { pick: QuickBorderPick }) {
  if (pick === 'none') {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: 'text.disabled',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '15%',
            width: '70%',
            height: 2,
            bgcolor: 'text.secondary',
            transform: 'translateY(-50%) rotate(-45deg)',
            borderRadius: 0.5,
          }}
        />
      </Box>
    );
  }
  if (pick === 'solid') {
    return <Box sx={{ width: 28, height: 4, bgcolor: 'text.primary', borderRadius: 0.5 }} />;
  }
  if (pick === 'dashed') {
    return (
      <Box
        sx={{
          width: 28,
          height: 8,
          boxSizing: 'border-box',
          borderBottom: '4px dashed',
          borderColor: 'text.primary',
        }}
      />
    );
  }
  if (pick === 'dotted') {
    return (
      <Box
        sx={{
          width: 28,
          height: 8,
          boxSizing: 'border-box',
          borderBottom: '4px dotted',
          borderColor: 'text.primary',
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 28,
        height: 10,
        boxSizing: 'border-box',
        borderBottom: '4px double',
        borderColor: 'text.primary',
      }}
    />
  );
}

function genId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function ensureBuilder(d: LandingConfig) {
  if (!d.builder || typeof d.builder !== 'object') {
    (d as LandingConfig).builder = { mode: 'canvas', sections: [] as BuilderSection[] };
  }
  const b = d.builder as Record<string, unknown>;
  if (!Array.isArray(b.sections)) b.sections = [];
  return b as { mode?: string; sections: BuilderSection[] };
}

function cssColorToHexApprox(input: string | undefined): string {
  if (!input || !String(input).trim()) return '#334155';
  const s = String(input).trim();
  if (/^#[0-9a-fA-F]{6}$/i.test(s)) return s.toLowerCase();
  const m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const r = Number(m[1]).toString(16).padStart(2, '0');
    const g = Number(m[2]).toString(16).padStart(2, '0');
    const b = Number(m[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return '#334155';
}

export type CanvasBlockToolbarProps = {
  block: BuilderBlock | null;
  sectionId: string | null;
  patch: (fn: (d: LandingConfig) => void) => void;
  onBlockDuplicated?: (newBlockId: string) => void;
  onBlockDeleted?: () => void;
  /** Mở vùng thư viện / kéo thả ở cột trái (rail mode). */
  onOpenChiTiet?: () => void;
  /** Canvas shapeText: gửi màu vào preview (tô đoạn đang bôi nếu có). */
  onRequestShapeTextToolbarColor?: (p: { sectionId: string; blockId: string; hex: string }) => void;
};

const CanvasBlockToolbar: React.FC<CanvasBlockToolbarProps> = ({
  block,
  sectionId,
  patch,
  onBlockDuplicated,
  onBlockDeleted,
  onOpenChiTiet,
  onRequestShapeTextToolbarColor,
}) => {
  const updateBlock = useCallback(
    (blockId: string, mut: (bl: BuilderBlock) => void) => {
      patch((d) => {
        const b = ensureBuilder(d);
        const sec = (b.sections as BuilderSection[]).find((s) => s.id === sectionId);
        if (!sec) return;
        const blk = sec.blocks.find((x) => x.id === blockId);
        if (blk) mut(blk);
      });
    },
    [patch, sectionId],
  );

  const duplicateBlock = useCallback((): string | null => {
    if (!sectionId || !block) return null;
    const newId = genId('blk');
    patch((d) => {
      const b = ensureBuilder(d);
      const sec = (b.sections as BuilderSection[]).find((s) => s.id === sectionId);
      if (!sec) return;
      const raw = JSON.parse(JSON.stringify(block)) as BuilderBlock;
      raw.id = newId;
      raw.x = Math.min(92, raw.x + 3);
      raw.y = Math.min(92, raw.y + 3);
      sec.blocks.push(raw);
    });
    return newId;
  }, [patch, block, sectionId]);

  const deleteBlock = useCallback(() => {
    if (!sectionId || !block) return;
    patch((d) => {
      const b = ensureBuilder(d);
      const sec = (b.sections as BuilderSection[]).find((s) => s.id === sectionId);
      if (sec) sec.blocks = sec.blocks.filter((x) => x.id !== block.id);
    });
    onBlockDeleted?.();
  }, [patch, block, sectionId, onBlockDeleted]);

  const [cornerAnchor, setCornerAnchor] = useState<HTMLElement | null>(null);
  const [borderAnchor, setBorderAnchor] = useState<HTMLElement | null>(null);
  const [opacityAnchor, setOpacityAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setCornerAnchor(null);
    setBorderAnchor(null);
    setOpacityAnchor(null);
  }, [block?.id, block?.type]);

  if (!block || !sectionId) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
      <Tooltip title="Nhân đôi phần tử">
        <IconButton
          size="small"
          onClick={() => {
            const nid = duplicateBlock();
            if (nid) onBlockDuplicated?.(nid);
          }}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Xóa phần tử">
        <IconButton size="small" color="error" onClick={deleteBlock}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      {block.type === 'button' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Nút
          </Typography>
          <ColorPickerPopover
            value={block.style.backgroundColor}
            onChange={(hex) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'button') bl.style.backgroundColor = hex;
              })
            }
            label="Màu nền"
            tooltip="Chọn màu nền nút"
          />
          <ColorPickerPopover
            value={block.style.color}
            onChange={(hex) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'button') bl.style.color = hex;
              })
            }
            label="Màu chữ"
            tooltip="Chọn màu chữ"
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Bo tròn góc">
            <IconButton
              size="small"
              color={cornerAnchor ? 'primary' : undefined}
              onClick={(e) => setCornerAnchor(e.currentTarget)}
            >
              <RoundedCorner fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Viền ngoài (không lấn vào nền)">
            <IconButton
              size="small"
              color={borderAnchor ? 'primary' : undefined}
              onClick={(e) => setBorderAnchor(e.currentTarget)}
            >
              <LineWeight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Trong suốt nền">
            <IconButton
              size="small"
              color={opacityAnchor ? 'primary' : undefined}
              onClick={(e) => setOpacityAnchor(e.currentTarget)}
            >
              <Opacity fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chữ nhỏ">
            <IconButton
              size="small"
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'button') bl.style.fontSize = Math.max(10, bl.style.fontSize - 1);
                })
              }
            >
              <RemoveIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chữ lớn">
            <IconButton
              size="small"
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'button') bl.style.fontSize = Math.min(48, bl.style.fontSize + 1);
                })
              }
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {block.type === 'media' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            {block.mediaKind === 'video' ? 'Video' : 'Ảnh/GIF'}
          </Typography>
          <ColorPickerPopover
            value={cssColorToHexApprox(block.style.backgroundColor)}
            onChange={(hex) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'media') bl.style.backgroundColor = hex;
              })
            }
            label="Nền khung"
            tooltip="Màu nền khung (letterbox)"
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Bo tròn góc">
            <IconButton
              size="small"
              color={cornerAnchor ? 'primary' : undefined}
              onClick={(e) => setCornerAnchor(e.currentTarget)}
            >
              <RoundedCorner fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Viền ngoài (không lấn vào nền)">
            <IconButton
              size="small"
              color={borderAnchor ? 'primary' : undefined}
              onClick={(e) => setBorderAnchor(e.currentTarget)}
            >
              <LineWeight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Trong suốt nền">
            <IconButton
              size="small"
              color={opacityAnchor ? 'primary' : undefined}
              onClick={(e) => setOpacityAnchor(e.currentTarget)}
            >
              <Opacity fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {block.type === 'shapeText' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Chữ
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150, maxWidth: 240 }}>
            <InputLabel id={`lp-tb-font-${block.id}`}>Font</InputLabel>
            <Select
              labelId={`lp-tb-font-${block.id}`}
              label="Font"
              value={canvasFontIdFromStack(block.style.fontFamily)}
              onChange={(e) => {
                const opt = LANDING_CANVAS_FONT_OPTIONS.find((o) => o.id === e.target.value);
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.fontFamily = opt?.stack ?? '';
                });
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {LANDING_CANVAS_FONT_OPTIONS.map((fo) => (
                <MenuItem key={fo.id} value={fo.id} sx={fo.stack ? { fontFamily: fo.stack } : undefined}>
                  {fo.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Căn trái">
            <IconButton
              size="small"
              color={block.style.textAlign === 'left' ? 'primary' : undefined}
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.textAlign = 'left';
                })
              }
            >
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Căn giữa">
            <IconButton
              size="small"
              color={block.style.textAlign === 'center' ? 'primary' : undefined}
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.textAlign = 'center';
                })
              }
            >
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Căn phải">
            <IconButton
              size="small"
              color={block.style.textAlign === 'right' ? 'primary' : undefined}
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.textAlign = 'right';
                })
              }
            >
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <ColorPickerPopover
            value={/^#/.test(block.style.color) ? block.style.color : '#ffffff'}
            onChange={(hex) => {
              if (onRequestShapeTextToolbarColor && sectionId) {
                onRequestShapeTextToolbarColor({ sectionId, blockId: block.id, hex });
                return;
              }
              updateBlock(block.id, (bl) => {
                if (bl.type === 'shapeText') bl.style.color = hex;
              });
            }}
            label="Màu chữ"
            tooltip="Trong preview (đang sửa chữ): kéo bôi đoạn — vùng chọn hiện nền xanh của trang; bấm màu ở đây hoặc chuột phải. Bấm ra ngoài vùng xanh để bỏ chọn. Không bôi thì đổi màu mặc định cả khung."
          />
          <ColorPickerPopover
            value={cssColorToHexApprox(block.style.backgroundColor)}
            onChange={(hex) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'shapeText') bl.style.backgroundColor = hex;
              })
            }
            label="Nền khung"
            tooltip="Màu nền khung chữ"
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          <Tooltip title="Bo tròn góc khung">
            <IconButton
              size="small"
              color={cornerAnchor ? 'primary' : undefined}
              onClick={(e) => setCornerAnchor(e.currentTarget)}
            >
              <RoundedCorner fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Viền ngoài (không lấn vào nền)">
            <IconButton
              size="small"
              color={borderAnchor ? 'primary' : undefined}
              onClick={(e) => setBorderAnchor(e.currentTarget)}
            >
              <LineWeight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Trong suốt nền">
            <IconButton
              size="small"
              color={opacityAnchor ? 'primary' : undefined}
              onClick={(e) => setOpacityAnchor(e.currentTarget)}
            >
              <Opacity fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chữ −">
            <IconButton
              size="small"
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.fontSize = Math.max(8, bl.style.fontSize - 1);
                })
              }
            >
              <RemoveIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chữ +">
            <IconButton
              size="small"
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'shapeText') bl.style.fontSize = Math.min(72, bl.style.fontSize + 1);
                })
              }
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {block.type === 'imageStrip' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Ảnh
          </Typography>
          <Tooltip title="Đổi chiều slide (ngang/dọc)">
            <IconButton
              size="small"
              onClick={() =>
                updateBlock(block.id, (bl) => {
                  if (bl.type === 'imageStrip') bl.orientation = bl.orientation === 'horizontal' ? 'vertical' : 'horizontal';
                })
              }
            >
              <SwapHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            type="number"
            label="Giây"
            value={block.speedSec}
            onChange={(e) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'imageStrip') bl.speedSec = Math.max(4, Number(e.target.value) || 18);
              })
            }
            sx={{ width: 76 }}
            InputProps={{ inputProps: { min: 4, step: 1 } }}
          />
        </>
      )}
      {block.type === 'imageSlider' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Cuộn
          </Typography>
          <TextField
            size="small"
            type="number"
            label="s/vòng"
            value={block.scrollSpeedSec ?? 24}
            onChange={(e) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'imageSlider') {
                  const n = Number(e.target.value);
                  bl.scrollSpeedSec = Number.isFinite(n) ? Math.max(4, Math.min(120, n)) : 24;
                }
              })
            }
            sx={{ width: 88 }}
            InputProps={{ inputProps: { min: 4, max: 120, step: 1 } }}
          />
        </>
      )}
      {block.type === 'eventList' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Lô
          </Typography>
          <TextField
            size="small"
            type="number"
            label="/lần"
            value={block.batchSize}
            onChange={(e) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'eventList') bl.batchSize = Math.max(1, Number(e.target.value) || 6);
              })
            }
            sx={{ width: 76 }}
            InputProps={{ inputProps: { min: 1 } }}
          />
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tooltip title="Chi tiết — mở / đóng panel cài đặt">
        <IconButton size="small" color="primary" onClick={() => onOpenChiTiet?.()}>
          <TuneOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(cornerAnchor)}
        anchorEl={cornerAnchor}
        onClose={() => setCornerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 280, borderRadius: 2, boxShadow: 3 },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Bo tròn góc
        </Typography>
        {block.type === 'button' && (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', maxWidth: 360 }}>
              <Slider
                size="small"
                min={0}
                max={MAX_CORNER_RADIUS_PX}
                value={Math.min(MAX_CORNER_RADIUS_PX, block.style.borderRadius)}
                onChange={(_, v) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'button')
                      bl.style.borderRadius = Math.min(MAX_CORNER_RADIUS_PX, v as number);
                  })
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}px`}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="px"
                value={Math.min(MAX_CORNER_RADIUS_PX, block.style.borderRadius)}
                onChange={(e) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'button')
                      bl.style.borderRadius = Math.max(
                        0,
                        Math.min(MAX_CORNER_RADIUS_PX, Number(e.target.value) || 0),
                      );
                  })
                }
                InputProps={{ inputProps: { min: 0, max: MAX_CORNER_RADIUS_PX } }}
                sx={{ width: 100, flexShrink: 0 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              0–{MAX_CORNER_RADIUS_PX}px
            </Typography>
          </Stack>
        )}
        {block.type === 'shapeText' && (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', maxWidth: 360 }}>
              <Slider
                size="small"
                min={0}
                max={MAX_CORNER_RADIUS_PX}
                value={Math.min(
                  MAX_CORNER_RADIUS_PX,
                  block.style.frameRadius ?? (block.shape === 'rounded' ? 12 : 4),
                )}
                onChange={(_, v) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'shapeText')
                      bl.style.frameRadius = Math.min(MAX_CORNER_RADIUS_PX, v as number);
                  })
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}px`}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="px"
                placeholder={block.shape === 'rounded' ? '12' : '4'}
                value={
                  block.style.frameRadius === undefined
                    ? ''
                    : Math.min(MAX_CORNER_RADIUS_PX, block.style.frameRadius)
                }
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  updateBlock(block.id, (bl) => {
                    if (bl.type !== 'shapeText') return;
                    if (raw === '') bl.style.frameRadius = undefined;
                    else
                      bl.style.frameRadius = Math.max(
                        0,
                        Math.min(MAX_CORNER_RADIUS_PX, Number(raw) || 0),
                      );
                  });
                }}
                InputProps={{ inputProps: { min: 0, max: MAX_CORNER_RADIUS_PX } }}
                sx={{ width: 100, flexShrink: 0 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              0–{MAX_CORNER_RADIUS_PX}px · để trống ô px = bo góc mặc định theo kiểu khung
            </Typography>
          </Stack>
        )}
        {block.type === 'media' && (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', maxWidth: 360 }}>
              <Slider
                size="small"
                min={0}
                max={MAX_CORNER_RADIUS_PX}
                value={Math.min(MAX_CORNER_RADIUS_PX, block.style.borderRadius)}
                onChange={(_, v) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'media')
                      bl.style.borderRadius = Math.min(MAX_CORNER_RADIUS_PX, v as number);
                  })
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}px`}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="px"
                value={Math.min(MAX_CORNER_RADIUS_PX, block.style.borderRadius)}
                onChange={(e) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'media')
                      bl.style.borderRadius = Math.max(
                        0,
                        Math.min(MAX_CORNER_RADIUS_PX, Number(e.target.value) || 0),
                      );
                  })
                }
                InputProps={{ inputProps: { min: 0, max: MAX_CORNER_RADIUS_PX } }}
                sx={{ width: 100, flexShrink: 0 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              0–{MAX_CORNER_RADIUS_PX}px
            </Typography>
          </Stack>
        )}
      </Popover>
      <Popover
        open={Boolean(borderAnchor)}
        anchorEl={borderAnchor}
        onClose={() => setBorderAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 300, borderRadius: 2, boxShadow: 3 },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Viền ngoài
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Viền vẽ bên ngoài vùng nền, không làm nhỏ phần màu bên trong.
        </Typography>
        {(block.type === 'button' || block.type === 'shapeText' || block.type === 'media') && (
          <Stack spacing={1.75}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={quickBorderFromModel(block.style.borderStyle, block.style.borderWidth)}
              onChange={(_, v) => {
                if (v == null || typeof v !== 'string') return;
                const pick = v as QuickBorderPick;
                updateBlock(block.id, (bl) => {
                  if (bl.type !== 'button' && bl.type !== 'shapeText' && bl.type !== 'media') return;
                  if (pick === 'none') {
                    bl.style.borderStyle = 'none';
                    bl.style.borderWidth = 0;
                  } else {
                    bl.style.borderStyle = pick;
                    if ((bl.style.borderWidth ?? 0) <= 0) bl.style.borderWidth = 2;
                  }
                });
              }}
              sx={{ flexWrap: 'wrap', gap: 0.75 }}
            >
              {(
                [
                  ['none', 'Không viền'],
                  ['solid', 'Nét liền'],
                  ['dashed', 'Gạch đứt'],
                  ['dotted', 'Chấm'],
                  ['double', 'Viền đôi'],
                ] as const
              ).map(([val, aria]) => (
                <ToggleButton
                  key={val}
                  value={val}
                  aria-label={aria}
                  sx={{
                    px: 1,
                    py: 0.75,
                    minWidth: 44,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <BorderStyleGlyph pick={val} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Độ đậm đường viền
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Slider
                size="small"
                min={0}
                max={24}
                value={block.style.borderWidth ?? 0}
                onChange={(_, v) => {
                  const n = v as number;
                  updateBlock(block.id, (bl) => {
                    if (bl.type !== 'button' && bl.type !== 'shapeText' && bl.type !== 'media') return;
                    bl.style.borderWidth = n;
                    if (n > 0 && bl.style.borderStyle === 'none') bl.style.borderStyle = 'solid';
                  });
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}px`}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                value={block.style.borderWidth ?? 0}
                onChange={(e) => {
                  const n = Math.max(0, Math.min(24, Number(e.target.value) || 0));
                  updateBlock(block.id, (bl) => {
                    if (bl.type !== 'button' && bl.type !== 'shapeText' && bl.type !== 'media') return;
                    bl.style.borderWidth = n;
                    if (n > 0 && bl.style.borderStyle === 'none') bl.style.borderStyle = 'solid';
                  });
                }}
                sx={{ width: 64 }}
                InputProps={{ inputProps: { min: 0, max: 24 } }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Màu viền
              </Typography>
              <ColorPickerPopover
                value={normalizeHex(block.style.borderColor)}
                onChange={(hex) =>
                  updateBlock(block.id, (bl) => {
                    if (bl.type === 'button' || bl.type === 'shapeText' || bl.type === 'media') bl.style.borderColor = hex;
                  })
                }
                label="Màu viền"
                tooltip="Chọn màu viền"
              />
            </Stack>
          </Stack>
        )}
      </Popover>
      <Popover
        open={Boolean(opacityAnchor)}
        anchorEl={opacityAnchor}
        onClose={() => setOpacityAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 260, borderRadius: 2, boxShadow: 3 },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Độ trong suốt nền
        </Typography>
        {(block.type === 'button' || block.type === 'shapeText' || block.type === 'media') && (
          <Slider
            size="small"
            min={0}
            max={100}
            value={Math.round((block.style.opacity ?? 1) * 100)}
            onChange={(_, v) =>
              updateBlock(block.id, (bl) => {
                if (bl.type === 'button' || bl.type === 'shapeText' || bl.type === 'media')
                  bl.style.opacity = Math.min(1, Math.max(0, (v as number) / 100));
              })
            }
            valueLabelDisplay="auto"
            valueLabelFormat={(x) => `${x}%`}
          />
        )}
      </Popover>
    </Stack>
    </Stack>
  );
};

export default CanvasBlockToolbar;
